import type {RefObject} from 'react'
import {Dialog} from '@primer/react'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'

import styles from './ResolveConflictsDialog.module.css'

/**
 * The `gh stack` command-line instructions dialog. Stacks can't resolve conflicts or rebase
 * onto a signature-required base branch from the web, so both the conflict-resolution CTA and
 * the rebase CTA point the viewer at the command line with this shared dialog.
 *
 * Presentational: the parent owns the trigger button and open state.
 */
export function ResolveConflictsDialog({
  stackNumber,
  returnFocusRef,
  onClose,
}: {
  stackNumber: number
  returnFocusRef: RefObject<HTMLButtonElement | null>
  onClose: () => void
}) {
  return (
    <Dialog
      className={styles.dialog}
      title="Resolve conflicts via the command line"
      returnFocusRef={returnFocusRef}
      onClose={onClose}
    >
      <p className="mb-2">
        Stacked pull requests rely on rebasing rather than merge commits to update your branch. Instead of merging
        upstream changes into your branch, a rebase will replay the commits from your head branch on top of the latest
        base.
      </p>
      {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case */}
      <h2 className="mb-2 h4">CLI Instructions</h2>
      <p className="mb-1">
        Use the <a href="https://gh.io/stacks">gh stack</a> CLI to resolve conflicts within your stack of branches.
      </p>
      <p className="mb-2 mt-3">
        <strong>Step 1</strong> Checkout the stack of branches locally.
      </p>
      <div className={styles.codeBlock}>
        <pre>
          <code>gh stack checkout {stackNumber}</code>
        </pre>
        <CopyToClipboardButton
          textToCopy={`gh stack checkout ${stackNumber}`}
          ariaLabel="Copy checkout command to clipboard"
          size="small"
        />
      </div>
      <p className="mb-2 mt-3">
        <strong>Step 2</strong> Rebase the stack with changes from the base branch.
      </p>
      <div className={styles.codeBlock}>
        <pre>
          <code>gh stack rebase</code>
        </pre>
        <CopyToClipboardButton textToCopy="gh stack rebase" ariaLabel="Copy rebase command to clipboard" size="small" />
      </div>
      <p className="mb-2 mt-3">
        <strong>Step 3</strong> Fix any conflicts and commit the result.
      </p>
      <p className="mt-2">
        See <a href="https://gh.io/stacks-resolving-conflicts">Resolving a merge conflict</a> for step-by-step
        instructions on resolving merge conflicts.
      </p>
      <p className="mb-2 mt-3">
        <strong>Step 4</strong> Push the changes.
      </p>
      <div className={styles.codeBlock}>
        <pre>
          <code>gh stack push</code>
        </pre>
        <CopyToClipboardButton textToCopy="gh stack push" ariaLabel="Copy push command to clipboard" size="small" />
      </div>
    </Dialog>
  )
}
